/**
 * @created     2026-02-27
 * @updated     2026-02-27
 * @description Standalone inline chat widget for the bot prototype.
 *              Renders a full-width chat panel with message history,
 *              an input field, a send button, and a "Neue Session" header button.
 *              On page reload the chat stays empty; the user starts fresh by
 *              clicking "Neue Session" or simply typing a message.
 *              Depends on: ChatService, ChatMessage/BotResponse models
 *              State: loading flag, message list (signals)
 */
import { TrackingService } from '../../core/services/tracking.service';
import { TRACKING_NODES, TrackingNode } from '../../core/services/tracking-nodes';
import {
  Component,
  signal,
  computed,
  ElementRef,
  ViewChild,
  AfterViewChecked,
  ChangeDetectionStrategy,
  inject,
  isDevMode,
  DestroyRef,
  EventEmitter,
  Output,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiErrorEnvelope, ChatService, SessionReportMetadata } from '../../core/services/chat.service';
import { BotResponse, ChatMessage, ChatAction, DebugCaseExport } from '../../core/models/bot-response.model';
import { QuickActionResponse } from '../../core/models/quick-action.model';
import { WidgetCardComponent } from '../../shared/components/widget-card/widget-card.component';
import { InsolvencyModalComponent } from '../../shared/components/insolvency-modal/insolvency-modal.component';
import { StammdatenChangePayload, StammdatenModalComponent } from '../../shared/components/stammdaten-modal/stammdaten-modal.component';
import { ContactFormModalComponent } from '../../shared/components/contact-form-modal/contact-form-modal.component';
import { DocumentUploadCompletePayload, DocumentUploadModalComponent } from '../../shared/components/document-upload-modal/document-upload-modal.component';
import { DeleteFormTodoModalComponent } from '../../shared/components/delete-form-todo-modal/delete-form-todo-modal.component';
import { SepaModalComponent, SepaMandat } from '../../shared/components/sepa-modal/sepa-modal.component';
import { I18nService } from '../../core/services/i18n.service';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { AkteData, KnowledgeService, safePrivacyUrl } from '../../core/services/knowledge.service';
import { navigatePaymentPopup, openPaymentPopup } from '../../shared/utils/payment-popup';

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, WidgetCardComponent, InsolvencyModalComponent, StammdatenModalComponent, ContactFormModalComponent, DocumentUploadModalComponent, DeleteFormTodoModalComponent, SepaModalComponent, TranslatePipe],
  templateUrl: './chat-widget.component.html',
  styleUrl: './chat-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatWidgetComponent implements AfterViewChecked {
  private readonly knowledgeService = inject(KnowledgeService);
  readonly stammdatenCurrentData = signal<AkteData | null>(null);
  private stammdatenOpening = 0;
  @Output() readonly reloginRequired = new EventEmitter<string | undefined>();

  @ViewChild('messageList') private messageList!: ElementRef<HTMLDivElement>;
  @ViewChild('chatInput') private chatInput!: ElementRef<HTMLTextAreaElement>;

  private readonly chatService = inject(ChatService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18n = inject(I18nService);

  // Debug export is a tester tool: never render it in production builds.
  readonly isDevMode = isDevMode;
  readonly isLoading = signal(false);
  readonly messages  = signal<ChatMessage[]>([]);
  readonly inputText = signal('');
  readonly hasError  = signal(false);
  readonly showInsolvencyModal = signal(false);
  readonly showStammdatenModal = signal(false);
  readonly showContactModal = signal(false);
  readonly contactPhoneRequired = signal(false);
  readonly showDocumentUploadModal = signal(false);
  readonly showDeleteFormTodoModal = signal(false);
  readonly showSepaModal = signal(false);
  readonly showSessionReportModal = signal(false);
  readonly sessionReportText = signal('');
  readonly sessionReportTesterName = signal('');
  readonly sessionReportStatus = signal<'idle' | 'success' | 'error'>('idle');
  readonly sessionReportSending = signal(false);
  readonly modalSubmitError = signal<string | null>(null);
  readonly modalData = signal<Record<string, any>>({});
  private readonly modalNonces: Record<string, string> = {};
  private pendingPaymentContractBridge: {
    modalId: 'payment_contract';
    nonce: string;
    actionType: 'payment';
    completionStep: 'installment_contract_review';
    completing: boolean;
  } | null = null;

  readonly canSend = computed(
    () => this.inputText().trim().length > 0 && !this.isLoading()
  );
  readonly isTerminalState = computed(
    () => this.messages().filter((message) => message.type === 'bot' && !message.systemNotice).at(-1)?.state === 'CLOSING'
  );
  readonly canSubmitSessionReport = computed(
    () => this.sessionReportText().trim().length > 0 && !this.sessionReportSending()
  );

  private static readonly MESSAGES_KEY = 'cr_chat_messages';
  private static readonly SKIP_PROOF_MESSAGE = 'weiter ohne nachweis';
  // Backend START sentinel (TurnProcessor); answer language comes from the request `language` field.
  private static readonly START_SENTINEL = 'Hallo';
  private static readonly ACTION_TRANSLATION_KEYS: Record<string, string> = {
    'Ich möchte die Forderung bezahlen': 'chat.actions.payClaim',
    'Ich möchte Details zu meiner Forderungsakte sehen': 'chat.actions.claimDetails',
    'Ich habe eine Rückfrage zur Forderung': 'chat.actions.claimDetails',
    'Ich möchte in Raten zahlen': 'chat.actions.installment',
    'Ich möchte eine Rate aussetzen': 'chat.actions.ratePause',
    'Ich brauche einen Zahlungsaufschub': 'chat.actions.deferral',
    'Ich möchte einen Vergleich anbieten': 'chat.actions.settlement',
    'Ich möchte einen Vergleich': 'chat.actions.settlement',
    'Ich möchte den gesamten Betrag auf einmal bezahlen': 'chat.actions.payTotal',
    'Ich möchte per Lastschrift zahlen': 'chat.actions.sepaDebit',
    'Ich möchte per Überweisung zahlen': 'chat.actions.transfer',
    'Ich möchte stattdessen per Überweisung zahlen': 'chat.actions.transferInstead',
    'weiter ohne nachweis': 'chat.actions.continueWithoutProof',
    'Ich möchte meinen Aktenstatus sehen': 'chat.actions.file',
    'Gibt es eine Ratenzahlungsvereinbarung?': 'chat.actions.installmentStatus',
    'Ich möchte eine Datenlöschung': 'chat.actions.dataDeletion',
    'Ich möchte Widerspruch einlegen': 'chat.actions.dispute',
    'Ich möchte einen Mangel melden': 'chat.actions.defect',
    'Ich möchte eine Ratenzahlung einrichten': 'chat.actions.setupInstallment',
    'Ich möchte jetzt bezahlen': 'chat.actions.payNow',
    'Ich möchte den angenommenen Vergleich per Überweisung zahlen': 'chat.actions.acceptedSettlementTransfer',
    'Mo-Fr, 9-12 Uhr': 'chat.actions.weekdayMorning',
    'Mo-Fr, 14-16 Uhr': 'chat.actions.weekdayAfternoon',
    '💰 Gesamtbetrag bezahlen': 'chat.actions.payTotal',
    '📅 In Raten zahlen': 'chat.actions.installment',
    '🏦 SEPA-Lastschrift': 'chat.actions.sepaDebit',
    '📋 Überweisung': 'chat.actions.transfer',
    'PayPal': 'chat.actions.paypal',
    '🏦 Lastschrift (SEPA)': 'chat.actions.sepaDebit',
    '🏦 Lastschrift': 'chat.actions.fillSepa',
    '📋 Stattdessen per Überweisung zahlen': 'chat.actions.transferInstead',
    '✅ Ratenzahlungsvereinbarung ausfüllen': 'chat.actions.fillContract',
    '📎 Dokument hochladen': 'chat.actions.uploadDocument',
    'Ohne Nachweis fortfahren': 'chat.actions.continueWithoutProof',
    '📧 Kontaktdaten eingeben': 'chat.actions.enterContact',
    'Kontaktdaten eingeben': 'chat.actions.enterContact',
    '📋 Forderungsakte': 'chat.actions.file',
    '📞 Kontakt CR': 'chat.actions.contactCr',
    '📩 Rückruf': 'chat.actions.callback',
    '🧾 Ratenstatus': 'chat.actions.installmentStatus',
    '✅ Schon bezahlt': 'chat.actions.alreadyPaid',
    '🗑️ Datenlöschung': 'chat.actions.dataDeletion',
    '❌ Widerspruch': 'chat.actions.dispute',
    '⚠️ Mangel melden': 'chat.actions.defect',
    '⚖️ Kosten / Recht': 'chat.actions.costsLaw',
    '💳 Jetzt bezahlen': 'chat.actions.payNow',
    '⏸️ Stundung': 'chat.actions.deferral',
    '🤝 Vergleich': 'chat.actions.settlement',
    '📋 Vergleich per Überweisung zahlen': 'chat.actions.acceptedSettlementTransfer',
    '💶 Ratenzahlung einrichten': 'chat.actions.setupInstallment',
    '💳 Forderung bezahlen': 'chat.actions.payNow',
    '📧 Prüfung anfragen': 'chat.actions.requestReview',
    '📝 Löschung beantragen': 'chat.actions.requestDeletion',
    '✅ Löschantrag abschließen': 'chat.actions.completeDeletion',
    '❌ Gesamte Forderung': 'chat.actions.fullDispute',
    '⚠️ Nur ein Teil': 'chat.actions.partialDispute',
    'Ja, neues Thema starten': 'chat.actions.startNewTopic',
    'Nein, hier bleiben': 'chat.actions.stayHere',
  };
  private static readonly ACTION_MODAL_KEYS: Record<string, string> = {
    sepa_mandate: 'chat.actions.fillSepa',
    payment_contract: 'chat.actions.fillContract',
    document_upload: 'chat.actions.uploadDocument',
    contact_form: 'chat.actions.enterContact',
    delete_form_todo: 'chat.actions.requestDeletion',
    insolvenz_popup: 'insolvencyModal.title',
    stammdaten: 'stammdatenModal.title',
  };
  private static readonly NONCE_PROTECTED_MODAL_IDS = new Set([
    'contact_form',
    'delete_form_todo',
    'document_upload',
    'payment_contract',
    'sepa_mandate',
    'insolvenz_popup',
    'stammdaten',
  ]);
  private quickActionQueue: string[] = [];
  private quickActionInFlight = false;

  @Output() readonly claimDetails = new EventEmitter<HTMLElement>();
  @Output() readonly quickAction = new EventEmitter<{ actionType: 'payment' | 'document_upload' | 'contact'; step?: string; payload?: Record<string, unknown> }>();

  constructor() {
    let previousLanguage = this.i18n.language();
    effect(() => {
      const currentLanguage = this.i18n.language();
      if (currentLanguage !== previousLanguage) {
        previousLanguage = currentLanguage;
        if (this.i18n.languageChangeSource() !== 'backend' && this.chatService.getSessionId()) {
          queueMicrotask(() => this.addMessage({
            type: 'bot',
            text: this.i18n.t('chat.languageChanged'),
            systemNotice: true,
            timestamp: new Date(),
          }));
        }
      }
    });

    this.chatService.quickActionRequests$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message) => {
        this.enqueueQuickAction(message);
      });

    this.tracking.chatOpen();
    const resume = () => {
      if (!this.handlePaypalReturnFromLocation() && this.messages().length === 0) {
        this.loadInitialGreeting();
      }
    };
    const stored = sessionStorage.getItem(ChatWidgetComponent.MESSAGES_KEY);
    if (stored && this.chatService.getSessionId()) {
      this.knowledgeService.getKnowledge().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          try {
            const parsed = JSON.parse(stored) as ChatMessage[];
            if (parsed.length > 0) {
              this.messages.set(parsed.map(m => ({ ...m, timestamp: new Date(m.timestamp) })));
              this.observeTrackingState(parsed.slice().reverse().find(m => m.type === 'bot')?.state ?? '', true, false);
            }
          } catch {
            // Corrupt data: start fresh.
          }
          resume();
        },
        error: (error) => {
          sessionStorage.removeItem(ChatWidgetComponent.MESSAGES_KEY);
          this.addApiErrorMessage(error);
        },
      });
      return;
    }
    resume();
  }

  private readonly tracking = inject(TrackingService);
  private trackingBotState = '';
  private trackingPaymentFlow: 'payment' | 'rzv' | null = null;

  readonly copyStatus = signal<'idle' | 'copied' | 'error'>('idle');
  readonly sessionIdStatus = signal<'idle' | 'copied' | 'error'>('idle');
  readonly copyLabel = computed(() => this.statusLabel(this.copyStatus(), 'chat.copyHistory'));
  readonly sessionIdLabel = computed(() => this.statusLabel(this.sessionIdStatus(), 'chat.copySessionId'));
  readonly menuOpen        = signal(false);

  toggleMenu(): void {
    this.menuOpen.update((v) => !v);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  isLatestBotMessage(messageIndex: number): boolean {
    const messages = this.messages();
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.type === 'bot' && !message.systemNotice) {
        return messageIndex === index;
      }
    }
    return false;
  }

  private statusLabel(status: 'idle' | 'copied' | 'error', idleKey: string): string {
    if (status === 'copied') {
      return `${this.i18n.t('common.copied')} ✓`;
    }

    if (status === 'error') {
      return this.i18n.t('chat.copyError');
    }

    return this.i18n.t(idleKey);
  }

  copySessionId(): void {
    const id = this.chatService.getSessionId();
    if (!id) return;
    navigator.clipboard.writeText(id).then(
      () => {
        this.sessionIdStatus.set('copied');
        setTimeout(() => this.sessionIdStatus.set('idle'), 2000);
      },
      () => {
        this.sessionIdStatus.set('error');
        setTimeout(() => this.sessionIdStatus.set('idle'), 2000);
      }
    );
  }

  addQuickActionConfirmation(message: string, debugExport?: DebugCaseExport): void {
    const text = message.trim();
    if (text === '') {
      return;
    }

    const lastMessage = this.messages().at(-1);
    if (lastMessage?.type === 'bot' && lastMessage.systemNotice === true && lastMessage.text === text) {
      return;
    }

    this.addMessage({
      type: 'bot',
      text,
      systemNotice: true,
      debugExport,
      timestamp: new Date(),
    });
  }

  openSessionReportModal(): void {
    this.sessionReportStatus.set('idle');
    this.showSessionReportModal.set(true);
  }

  closeSessionReportModal(): void {
    if (this.sessionReportSending()) {
      return;
    }

    this.showSessionReportModal.set(false);
    this.sessionReportText.set('');
    this.sessionReportTesterName.set('');
    this.sessionReportStatus.set('idle');
    setTimeout(() => this.chatInput?.nativeElement?.focus());
  }

  onSessionReportTextChange(value: string): void {
    this.sessionReportText.set(value);
    if (this.sessionReportStatus() !== 'idle') {
      this.sessionReportStatus.set('idle');
    }
  }

  onSessionReportTesterNameChange(value: string): void {
    this.sessionReportTesterName.set(value);
    if (this.sessionReportStatus() !== 'idle') {
      this.sessionReportStatus.set('idle');
    }
  }

  sessionReportSecondaryLabel(): string {
    return this.i18n.t(this.sessionReportStatus() === 'success' ? 'common.close' : 'common.cancel');
  }

  submitSessionReport(): void {
    const message = this.sessionReportText().trim();
    if (!message || this.sessionReportSending()) {
      return;
    }

    this.sessionReportSending.set(true);
    this.sessionReportStatus.set('idle');

    this.chatService.reportSession(message, this.sessionReportMetadata()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.sessionReportSending.set(false);
        this.sessionReportStatus.set('success');
        this.sessionReportText.set('');
        this.sessionReportTesterName.set('');
      },
      error: (err) => {
        if (isDevMode()) {
          console.error('❌ [CHAT-WIDGET] Session-Report Fehler:', err);
        }
        this.sessionReportSending.set(false);
        this.sessionReportStatus.set('error');
      },
    });
  }

  private shouldScrollToBottom = false;

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  onInputChange(value: string): void {
    this.inputText.set(value);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  send(): void {
    const text = this.inputText().trim();
    if (!text || this.isLoading()) {
      return;
    }

    this.inputText.set('');
    this.dispatchUserMessage(text);
  }

  onActionClick(action: ChatAction, messageIndex?: number): void {
    if (this.isLoading() && this.showStammdatenModal()) return;
    if (messageIndex !== undefined && !this.isLatestBotMessage(messageIndex)) {
      return;
    }

    if (isDevMode()) {
      console.log('🧠 [BOT] Aktion geklickt:', action);
    }

    if (action.type === 'send_message') {
      if (this.trackingBotState === 'PAY_UE_BANK') this.tracking.submitFlow('payment');
      this.dispatchUserMessage(action.payload, undefined, this.localizedActionLabel(action));
    } else if (action.type === 'modal') {
      this.openModal(action);
    } else if (action.type === 'paypal') {
      this.startPaypalPayment(action);
    } else if (action.type === 'quick_action') {
      this.quickAction.emit({
        actionType: action.actionType ?? 'payment',
        step: action.step,
        payload: action.stepPayload ?? {},
      });
    }
  }

  private startPaypalPayment(action: ChatAction): void {
    if (this.isLoading()) {
      return;
    }

    const popup = openPaymentPopup();
    if (!popup) {
      this.hasError.set(true);
      this.addMessage({
        type: 'bot',
        text: this.i18n.t('chat.paypalPopupBlocked'),
        systemNotice: true,
        timestamp: new Date(),
      });
      return;
    }

    this.addMessage({
      type: 'user',
      text: this.localizedActionLabel(action),
      timestamp: new Date(),
    });
    this.isLoading.set(true);
    this.hasError.set(false);

    this.chatService.createPaypalOrder().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        this.tracking.paypalRedirect(popup);
        navigatePaymentPopup(popup, response.data.approval_url);
      },
      error: (error) => {
        this.tracking.error('network', 'payment');
        popup.close();
        this.isLoading.set(false);
        this.hasError.set(true);
        this.addApiErrorMessage(error, 'chat.paypalStartError', true);
      },
    });
  }

  private handlePaypalReturnFromLocation(): boolean {
    const locationRef = globalThis.location;
    const status = new URLSearchParams(locationRef.search).get('paypal');
    if (status !== 'return' && status !== 'cancel') {
      return false;
    }

    this.tracking.paypalReturn();
    const params = new URLSearchParams(locationRef.search);
    const orderId = params.get('token')?.trim() ?? '';
    this.clearPaypalReturnParams(params);

    if (status === 'cancel') {
      this.tracking.abortFlow('payment');
      this.addMessage({
        type: 'bot',
        text: this.i18n.t('chat.paypalCancelled'),
        systemNotice: true,
        timestamp: new Date(),
      });
      return true;
    }

    if (!orderId) {
      this.tracking.error('validation', 'payment');
      this.hasError.set(true);
      this.addMessage({
        type: 'bot',
        text: this.i18n.t('chat.paypalReturnError'),
        systemNotice: true,
        timestamp: new Date(),
      });
      return true;
    }

    this.isLoading.set(true);
    this.hasError.set(false);

    this.tracking.submitFlow('payment');
    this.chatService.capturePaypalOrder(orderId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        if (response.success && response.data.status === 'COMPLETED') this.tracking.completeFlow('payment');
        else this.tracking.error('validation', 'payment');
        this.isLoading.set(false);
        this.addQuickActionConfirmation(response.data.completion_message ?? '', response.data.debugExport);
      },
      error: (error) => {
        this.tracking.error('network', 'payment');
        this.isLoading.set(false);
        this.hasError.set(true);
        this.addApiErrorMessage(error, 'chat.paypalCaptureError', true);
      },
    });

    return true;
  }

  private clearPaypalReturnParams(params: URLSearchParams): void {
    const url = new URL(globalThis.location.href);
    for (const key of ['paypal', 'token', 'PayerID']) {
      url.searchParams.delete(key);
    }

    globalThis.history?.replaceState(globalThis.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    params.delete('paypal');
    params.delete('token');
    params.delete('PayerID');
  }

  localizedActionLabel(action: ChatAction): string {
    const key = this.actionTranslationKey(action);
    return key ? this.i18n.t(key) : action.label;
  }

  /** Opens a modal based on the action's modalId */
  openModal(action: ChatAction): void {
    const modalId = action.modalId ?? action.payload;
    this.modalSubmitError.set(null);

    if (modalId === 'payment_contract') {
      this.setModalNonce(modalId, action.nonce);
      const nonce = this.requireModalNonce(modalId);
      if (!nonce) {
        this.pendingPaymentContractBridge = null;
        return;
      }
      this.pendingPaymentContractBridge = {
        modalId,
        nonce,
        actionType: 'payment',
        completionStep: 'installment_contract_review',
        completing: false,
      };
      this.openInstallmentWizard(action, nonce);
      return;
    }

    this.setModalNonce(modalId, action.nonce);

    if (modalId === 'insolvenz_popup') {
      this.showInsolvencyModal.set(true);
    } else if (modalId === 'stammdaten') {
      const opening = ++this.stammdatenOpening;
      this.tracking.startFlow('master_data');
      this.stammdatenCurrentData.set(null);
      this.showStammdatenModal.set(true);
      const sessionId = this.chatService.getSessionId();
      if (!sessionId) return;
      const isCurrentOpening = () => this.showStammdatenModal()
        && this.stammdatenOpening === opening && this.chatService.getSessionId() === sessionId;
      this.knowledgeService.getKnowledge().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: data => {
          if (isCurrentOpening()) {
            this.stammdatenCurrentData.set(data.source === 'db' ? data.akte ?? null : null);
          }
        },
        error: () => {
          if (isCurrentOpening()) this.stammdatenCurrentData.set(null);
        },
      });
    } else if (modalId === 'contact_form') {
      this.contactPhoneRequired.set(action.requiresPhone === true);
      this.showContactModal.set(true);
    } else if (modalId === 'document_upload') {
      this.showDocumentUploadModal.set(true);
	    } else if (modalId === 'delete_form_todo') {
	      this.showDeleteFormTodoModal.set(true);
	    } else if (modalId === 'sepa_mandate') {
	      this.modalData.set(action.modalData ?? {});
	      this.showSepaModal.set(true);
    }
  }

  private openInstallmentWizard(action: ChatAction, nonce: string): void {
    const modalData = action.modalData ?? {};
    const paymentMethod = this.installmentPaymentMethod(action, modalData);
    const durationMonths = this.installmentDurationMonths(modalData);
    const start = typeof modalData['start'] === 'string' ? modalData['start'].trim() : '';

    if (start && durationMonths && paymentMethod) {
      this.quickAction.emit({
        actionType: 'payment',
        step: 'installment_terms',
        payload: {
          start,
          duration_months: durationMonths,
          payment_method: paymentMethod,
          modal_nonce: nonce,
        },
      });
      return;
    }

    this.quickAction.emit({
      actionType: 'payment',
      step: 'payment_method',
      payload: { method: paymentMethod ?? 'installments', modal_nonce: nonce },
    });
  }

  private installmentPaymentMethod(action: ChatAction, modalData: Record<string, any>): 'transfer' | 'sepa' | null {
    const value = action.stepPayload?.['payment_method']
      ?? action.stepPayload?.['method']
      ?? modalData['payment_method'];

    return value === 'transfer' || value === 'sepa' ? value : null;
  }

  private installmentDurationMonths(modalData: Record<string, any>): number | null {
    const value = modalData['duration_months'];
    const min = modalData['minDurationMonths'];
    const max = modalData['maxDurationMonths'];
    return Number.isInteger(value) && Number.isInteger(min) && Number.isInteger(max)
      && min > 0 && value >= min && value <= max ? value : null;
  }

  /** Returns true when the chat modal bridge owns the confirmation for this completion. */
  completePendingPaymentContract(response: QuickActionResponse): boolean {
    const bridge = this.pendingPaymentContractBridge;
    if (!bridge
      || response.status !== 'completed'
      || response.action_type !== bridge.actionType
      || response.current_step !== bridge.completionStep
      || response.metadata?.['completion_type'] !== 'installment_contract'
    ) {
      return false;
    }
    if (bridge.completing) {
      return true;
    }

    bridge.completing = true;
    this.isLoading.set(true);
    this.chatService.sendModalComplete(bridge.modalId, bridge.nonce, { contractAccepted: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        if (this.pendingPaymentContractBridge === bridge) {
          this.pendingPaymentContractBridge = null;
          this.clearModalNonce(bridge.modalId);
        }
        this.addMessage(this.botMessage(result));
        this.isLoading.set(false);
        this.scheduleScrollToBottom();
      },
      error: (error) => {
        bridge.completing = false;
        this.addApiErrorMessage(error);
        this.isLoading.set(false);
      },
    });
    return true;
  }

  cancelPendingPaymentContract(): void {
    const bridge = this.pendingPaymentContractBridge;
    if (!bridge || bridge.completing) {
      return;
    }

    this.pendingPaymentContractBridge = null;
    this.clearModalNonce(bridge.modalId);
  }

  submitInsolvency(data: Record<string, string | null>): void {
    if (this.isLoading()) return;
    const nonce = this.requireModalNonce('insolvenz_popup');
    if (!nonce) return;
    this.isLoading.set(true);
    this.chatService.sendModalComplete('insolvenz_popup', nonce, data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res.state === 'INSOLVENCY_DONE' && res.validation?.['valid'] !== false) {
          this.showInsolvencyModal.set(false);
          this.clearModalNonce('insolvenz_popup');
        } else {
          this.openAutoOpenModal(res);
          this.modalSubmitError.set(res.response);
        }
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
      },
      error: (err) => {
        this.addApiErrorMessage(err);
        this.isLoading.set(false);
      },
    });
  }

  /** Cancels the open stammdaten flow with its issued nonce. */
  closeStammdatenModal(): void {
    if (this.isLoading() || !this.showStammdatenModal()) return;
    const nonce = this.requireModalNonce('stammdaten');
    if (!nonce) return;
    this.isLoading.set(true);
    this.chatService.sendModalComplete('stammdaten', nonce, { operation: 'cancel' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res.state === 'MENU' && res.validation?.['valid'] !== false) {
          this.tracking.abortFlow('master_data');
          this.stammdatenCurrentData.set(null);
          this.showStammdatenModal.set(false);
          this.modalSubmitError.set(null);
          this.clearModalNonce('stammdaten');
          setTimeout(() => this.chatInput?.nativeElement?.focus());
        } else {
          this.openAutoOpenModal(res);
          this.modalSubmitError.set(res.response);
        }
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
      },
      error: (err) => {
        this.modalSubmitError.set(this.addApiErrorMessage(err));
        this.isLoading.set(false);
      },
    });
  }

  /** Submits stammdaten changes and notifies the backend */
  submitStammdatenChange(data: StammdatenChangePayload): void {
    if (this.isLoading()) return;
    const modalNonce = this.requireModalNonce('stammdaten');
    if (!modalNonce) return;

    this.showStammdatenModal.set(false);
    this.isLoading.set(true);

    this.tracking.submitFlow('master_data');
    this.chatService.sendModalComplete('stammdaten', modalNonce, data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res.state === 'INFO_TRANSFER' && res.validation?.['valid'] !== false) this.tracking.completeFlow('master_data');
        else this.tracking.error('validation', 'master_data');
        this.clearModalNonce('stammdaten');
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
      error: (err) => {
        this.addApiErrorMessage(err);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
    });
  }

  /** Closes the contact form modal and notifies the backend */
  closeContactModal(data?: { email: string; telefon: string; callback_time_slot?: string }): void {
    if (!data) {
      this.tracking.abortFlow('rate_pause');
      if (this.trackingPaymentFlow) this.tracking.abortFlow(this.trackingPaymentFlow);
      this.showContactModal.set(false);
      this.modalSubmitError.set(null);
      this.clearModalNonce('contact_form');
      return; // User cancelled, no backend notification
    }

    if (this.isLoading()) return;

    const modalNonce = this.requireModalNonce('contact_form');
    if (!modalNonce) return;

    this.showContactModal.set(false);
    this.isLoading.set(true);

    if (['RATENAUSSETZUNG_CONTACT', 'RATENAUSSETZUNG_CONFIRM_CONTACT'].includes(this.trackingBotState)) this.tracking.submitFlow('rate_pause');
    if (this.trackingBotState === 'PAY_CONTACT' && this.trackingPaymentFlow) this.tracking.submitFlow(this.trackingPaymentFlow);
    this.chatService.sendModalComplete('contact_form', modalNonce, data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.clearModalNonce('contact_form');
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
        this.scheduleScrollToBottom();
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
      error: (err) => {
        this.addApiErrorMessage(err);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
    });
  }

  /** Closes SEPA modal without submitting */
  closeSepaModal(): void {
    if (this.trackingPaymentFlow) this.tracking.abortFlow(this.trackingPaymentFlow);
    this.showSepaModal.set(false);
    this.modalSubmitError.set(null);
  }

  /** Closes the temporary delete-form modal and optionally advances the flow */
  closeDeleteFormTodoModal(completed = false): void {
    if (!completed) {
      this.showDeleteFormTodoModal.set(false);
      this.modalSubmitError.set(null);
      this.clearModalNonce('delete_form_todo');
      setTimeout(() => this.chatInput?.nativeElement?.focus());
      return;
    }

    const modalNonce = this.requireModalNonce('delete_form_todo');
    if (!modalNonce) return;

    this.showDeleteFormTodoModal.set(false);
    this.isLoading.set(true);
    this.chatService.sendModalComplete('delete_form_todo', modalNonce).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.clearModalNonce('delete_form_todo');
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
        this.scheduleScrollToBottom();
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
      error: (err) => {
        this.addApiErrorMessage(err);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
    });
  }

  /** Submits SEPA mandate and advances the flow */
  submitSepaMandat(data: SepaMandat): void {
    const nonce = this.requireModalNonce('sepa_mandate');
    if (!nonce) return;

    this.showSepaModal.set(false);
    this.isLoading.set(true);

    // Full payment submits with the mandate; in rzv it is an intermediate step before the contact form.
    if (this.trackingPaymentFlow === 'payment') this.tracking.submitFlow('payment');
    this.chatService.sendModalComplete('sepa_mandate', nonce, data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.clearModalNonce('sepa_mandate');
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
        this.scheduleScrollToBottom();
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
      error: (err) => {
        this.addApiErrorMessage(err);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
    });
  }

  /** Skips document upload (close/X button) — sends "weiter ohne nachweis" */
  skipDocumentUpload(): void {
    this.showDocumentUploadModal.set(false);
    this.modalSubmitError.set(null);
    this.clearModalNonce('document_upload');
    this.isLoading.set(true);

    this.chatService.sendMessage(ChatWidgetComponent.SKIP_PROOF_MESSAGE).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
        this.scheduleScrollToBottom();
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
      error: (err) => {
        this.addApiErrorMessage(err);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
    });
  }

  documentUploadNonce(): string | undefined {
    return this.getModalNonce('document_upload');
  }

  /** Completes document upload and notifies backend via modal-complete */
  closeDocumentUploadModal(data?: DocumentUploadCompletePayload): void {
    if (!data) {
      this.showDocumentUploadModal.set(false);
      this.modalSubmitError.set(null);
      return;
    }

    const modalNonce = this.requireModalNonce('document_upload');
    if (!modalNonce) return;

    this.showDocumentUploadModal.set(false);
    this.isLoading.set(true);

    this.chatService.sendModalComplete('document_upload', modalNonce, data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.clearModalNonce('document_upload');
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
        this.scheduleScrollToBottom();
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
      error: (err) => {
        this.addApiErrorMessage(err);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
    });
  }

  /**
   * Copies the current conversation to clipboard in a readable format.
   */
  copyChat(): void {
    const lines = this.messages().map((msg) => {
      const sender = msg.type === 'bot' ? this.i18n.t('chat.senderBot') : this.i18n.t('chat.senderUser');
      return `${sender}: ${msg.text}`;
    });

    const sid = this.chatService.getSessionId() ?? this.i18n.t('chat.sessionUnknown');
    const text = `<ChatProtokoll session-id="${sid}">\n${lines.join('\n\n')}\n</ChatProtokoll>`;

    navigator.clipboard.writeText(text).then(
      () => {
        this.copyStatus.set('copied');
        setTimeout(() => this.copyStatus.set('idle'), 2000);
      },
      () => {
        this.copyStatus.set('error');
        setTimeout(() => this.copyStatus.set('idle'), 2000);
      }
    );
  }

  /**
   * Creates a fresh authenticated chat session while keeping the case login.
   */
  startNewSession(): void {
    for (const flow of ['payment', 'rzv', 'rate_pause', 'master_data'] as const) this.tracking.abortFlow(flow);
    this.trackingPaymentFlow = null;
    this.trackingBotState = '';
    if (this.isLoading()) {
      return;
    }

    this.tracking.resetChatContext();
    this.messages.set([]);
    this.hasError.set(false);
    this.quickActionQueue = [];
    this.quickActionInFlight = false;
    this.showInsolvencyModal.set(false);
    this.showStammdatenModal.set(false);
    this.showContactModal.set(false);
    this.showDocumentUploadModal.set(false);
    this.showDeleteFormTodoModal.set(false);
    this.showSepaModal.set(false);
    this.showSessionReportModal.set(false);
    this.sessionReportText.set('');
    this.sessionReportTesterName.set('');
    this.sessionReportStatus.set('idle');
    this.sessionReportSending.set(false);
    this.modalSubmitError.set(null);
    this.modalData.set({});
    this.pendingPaymentContractBridge = null;
    for (const modalId of Object.keys(this.modalNonces)) {
      delete this.modalNonces[modalId];
    }
    sessionStorage.removeItem(ChatWidgetComponent.MESSAGES_KEY);
    this.isLoading.set(true);

    if (isDevMode()) {
      console.log('🔄 [CHAT-WIDGET] Neue Session gestartet');
    }

    this.chatService.createNewSession().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.loadInitialGreeting();
      },
      error: (err) => {
        this.addApiErrorMessage(err, 'chat.newSessionError');
        this.hasError.set(true);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
      },
    });
  }

  private loadInitialGreeting(): void {
    this.isLoading.set(true);

    this.chatService.sendMessage(ChatWidgetComponent.START_SENTINEL).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.addMessage(this.botMessage(res));
        this.isLoading.set(false);
        this.processQuickActionQueue();
      },
      error: (error) => {
        this.addApiErrorMessage(error, 'chat.connectionError');
        this.isLoading.set(false);
        this.processQuickActionQueue();
      },
    });
  }

  private enqueueQuickAction(message: string): void {
    const normalized = message.trim();
    if (!normalized) {
      return;
    }
    this.quickActionQueue.push(normalized);
    this.processQuickActionQueue();
  }

  private processQuickActionQueue(): void {
    if (this.isLoading() || this.quickActionInFlight) {
      return;
    }

    const nextAction = this.quickActionQueue.shift();
    if (!nextAction) {
      return;
    }

    this.quickActionInFlight = true;
    const done = (): void => {
      this.quickActionInFlight = false;
      this.processQuickActionQueue();
    };

    if (this.messages().length === 0) {
      this.dispatchUserMessage(
        ChatWidgetComponent.START_SENTINEL,
        () => this.dispatchUserMessage(nextAction, done, this.localizedQueuedAction(nextAction)),
        this.i18n.t('chat.initialGreeting'),
      );
      return;
    }

    this.dispatchUserMessage(nextAction, done, this.localizedQueuedAction(nextAction));
  }

  private dispatchUserMessage(
    text: string,
    onComplete?: () => void,
    visibleText?: string,
  ): void {
    const normalized = text.trim();
    if (!normalized || this.isLoading()) {
      onComplete?.();
      return;
    }

    this.tracking.chatMessage();
    this.hasError.set(false);
    this.addMessage({ type: 'user', text: visibleText?.trim() || normalized, timestamp: new Date() });
    this.isLoading.set(true);

    setTimeout(() => this.chatInput?.nativeElement?.focus());

    if (isDevMode()) {
      console.log('🚀 [CHAT-WIDGET] Sende Nachricht:', normalized);
    }

    this.chatService.sendMessage(normalized).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.addMessage(this.botMessage(res));
        this.openAutoOpenModal(res);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
        onComplete?.();
        this.processQuickActionQueue();
      },
      error: (err) => {
        this.addApiErrorMessage(err);
        this.hasError.set(true);
        this.isLoading.set(false);
        setTimeout(() => this.chatInput?.nativeElement?.focus());
        onComplete?.();
        this.processQuickActionQueue();
      },
    });
  }

  private addApiErrorMessage(error: unknown, fallbackKey = 'chat.genericError', systemNotice = false): string {
    this.tracking.error('network');
    const message = this.chatErrorMessage(error, fallbackKey);
    const contactAvailable = ['VALIDATION_FAILED', 'KNOWLEDGE_NOT_FOUND', 'PAYPAL_NOT_ALLOWED', 'PAYPAL_ORDER_NOT_FOUND'].includes(this.chatErrorCode(error) ?? '');
    if (contactAvailable) {
      this.messages.set([]);
      sessionStorage.removeItem(ChatWidgetComponent.MESSAGES_KEY);
      this.showStammdatenModal.set(false);
      this.showDocumentUploadModal.set(false);
      this.showDeleteFormTodoModal.set(false);
      this.showSepaModal.set(false);
      this.modalData.set({});
      this.pendingPaymentContractBridge = null;
    }
    this.addMessage({
      type: 'bot',
      text: message,
      timestamp: new Date(),
      ...(systemNotice ? { systemNotice: true } : {}),
      ...(contactAvailable ? { actions: [{ type: 'quick_action' as const, label: this.i18n.t('chat.actions.contactForm'), payload: 'contact_form', actionType: 'contact' as const }] } : {}),
    });
    if (this.chatErrorCode(error) === 'SESSION_REPLACED') {
      this.reloginRequired.emit(message);
    }

    return message;
  }

  private sessionReportMetadata(): SessionReportMetadata {
    const location = globalThis.location;
    const navigatorRef = globalThis.navigator;
    const testerName = this.sessionReportTesterName().trim();

    return {
      url: location?.href,
      path: location ? `${location.pathname}${location.search}${location.hash}` : undefined,
      language: this.i18n.language(),
      user_agent: navigatorRef?.userAgent,
      ...(testerName ? { tester_name: testerName } : {}),
      chat_history: this.formatChatHistory(),
    };
  }

  private formatChatHistory(): string {
    return this.messages()
      .map((msg) => `${msg.type === 'bot' ? 'BOT' : 'User'}: ${msg.text}`)
      .join('\n\n');
  }

  private chatErrorCode(error: unknown): string | null {
    const envelope = this.errorEnvelope(error);
    if (!envelope) {
      return null;
    }

    if (typeof envelope.code === 'string' && envelope.code) {
      return envelope.code;
    }

    const nestedError = envelope.error;
    if (nestedError && typeof nestedError === 'object') {
      const nestedCode = (nestedError as { code?: unknown }).code;
      return typeof nestedCode === 'string' && nestedCode ? nestedCode : null;
    }

    return null;
  }

  private chatErrorMessage(error: unknown, fallbackKey = 'chat.genericError'): string {
    const envelope = this.errorEnvelope(error);
    const code = this.chatErrorCode(error);
    if (['VALIDATION_FAILED', 'KNOWLEDGE_NOT_FOUND', 'PAYPAL_NOT_ALLOWED', 'PAYPAL_ORDER_NOT_FOUND', 'PAYPAL_DISABLED'].includes(code ?? '') && typeof envelope?.message === 'string') {
      return envelope.message;
    }
    const localizedByCode = code ? this.localizedErrorMessage(`chat.errors.${code}`) : null;
    const message = localizedByCode ?? this.i18n.t(fallbackKey);
    for (const candidate of [envelope?.requestId, envelope?.meta?.requestId]) {
      // Match the backend RequestCorrelation format; never interpret error text.
      const requestId = typeof candidate === 'string' ? candidate.trim() : '';
      if (/^[A-Za-z0-9_.:-]{1,128}$/.test(requestId)) {
        return `${message} ${this.i18n.t('chat.supportReference')}: ${requestId}`;
      }
    }

    return message;
  }

  private localizedErrorMessage(key: string): string | null {
    const translated = this.i18n.t(key);
    return translated !== key ? translated : null;
  }

  private errorEnvelope(error: unknown): ApiErrorEnvelope | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const body = (error as { error?: unknown }).error;
    return body && typeof body === 'object'
      ? body as ApiErrorEnvelope
      : null;
  }

  private addMessage(message: ChatMessage): void {
    this.messages.update((msgs) => {
      const updated = [...msgs, message];
      this.persistMessages(updated);
      return updated;
    });
    this.scheduleScrollToBottom();
  }

  private observeTrackingState(state: string, accepted = true, observeNode = true): void {
    if (observeNode && Object.hasOwn(TRACKING_NODES, state) && (accepted || !TRACKING_NODES[state as TrackingNode])) {
      try { this.tracking.chatNode(state as TrackingNode); } catch { /* Best-effort observation only. */ }
    }
    this.trackingBotState = state;
    if (!accepted) { this.tracking.error('validation'); return; }
    const paymentFlow = ['PAY_VZ_OPTIONS', 'PAY_VZ_BANK', 'PAY_UE_BANK', 'PAY_SEPA_FORM', 'PAY_SEPA_ERROR'].includes(state) ? 'payment'
      : ['PAY_TZ_CHECK', 'PAY_TZ_OFFER', 'PAY_TZ_SEPA_FORM', 'PAY_TZ_UE_BANK', 'PAY_RZV_CONTRACT', 'PAY_RZV_MINCHECK'].includes(state) ? 'rzv' : null;
    if (paymentFlow) {
      if (this.trackingPaymentFlow && this.trackingPaymentFlow !== paymentFlow) this.tracking.abortFlow(this.trackingPaymentFlow);
      this.trackingPaymentFlow = paymentFlow;
      this.tracking.startFlow(paymentFlow);
    } else if (state === 'PAY_TRANSFER' && this.trackingPaymentFlow) {
      // A confirmed terminal response is the submit for paths without an earlier submit hook.
      this.tracking.submitFlow(this.trackingPaymentFlow);
      this.tracking.completeFlow(this.trackingPaymentFlow);
      this.trackingPaymentFlow = null;
    } else if (!['PAY_CONTACT', 'PAY_SEPA_CHECK'].includes(state) && this.trackingPaymentFlow) {
      this.tracking.abortFlow(this.trackingPaymentFlow);
      this.trackingPaymentFlow = null;
    }
    if (['RATENAUSSETZUNG_START', 'RATENAUSSETZUNG_INFO', 'RATENAUSSETZUNG_NOTICE', 'RATENAUSSETZUNG_CONTACT', 'RATENAUSSETZUNG_CONFIRM_CONTACT'].includes(state)) {
      this.tracking.startFlow('rate_pause');
    } else if (['RATENAUSSETZUNG_CONFIRM', 'RATENAUSSETZUNG_TRANSFER'].includes(state)) {
      // A confirmed terminal response is the submit for paths without the contact form.
      this.tracking.submitFlow('rate_pause');
      this.tracking.completeFlow('rate_pause');
    } else {
      this.tracking.abortFlow('rate_pause');
    }
  }

  private botMessage(res: BotResponse): ChatMessage {
    this.observeTrackingState(res.state, res.validation?.['valid'] !== false);
    return {
      type: 'bot',
      text: res.response,
      welcome: res.welcome,
      state: res.state,
      actions: res.actions,
      widgets: res.widgets,
      debugExport: res.debugExport,
      timestamp: new Date(),
    };
  }

  privacyLink(message: ChatMessage): { url: string; before: string; after: string } | null {
    const url = safePrivacyUrl(message.welcome?.privacyUrl);
    const index = url ? message.text.indexOf(url) : -1;
    return url && index >= 0 ? { url, before: message.text.slice(0, index), after: message.text.slice(index + url.length) } : null;
  }

  private openAutoOpenModal(res: BotResponse): void {
    const action = res.actions?.find((candidate) => candidate.type === 'modal' && candidate.autoOpen === true
      && (candidate.modalId ?? candidate.payload) !== 'insolvenz_popup');
    if (action) {
      this.openModal(action);
    }
  }

  private scheduleScrollToBottom(): void {
    this.shouldScrollToBottom = true;
    setTimeout(() => this.scrollToBottom());
    setTimeout(() => this.scrollToBottom(), 120);
  }

  private persistMessages(messages: ChatMessage[]): void {
    try {
      sessionStorage.setItem(ChatWidgetComponent.MESSAGES_KEY, JSON.stringify(messages));
    } catch {
      // sessionStorage full or unavailable — ignore
    }
  }

  private scrollToBottom(): void {
    try {
      const el = this.messageList?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    } catch {
      // ignore scroll errors
    }
  }

  private setModalNonce(modalId: string, nonce?: string): void {
    if (nonce) {
      this.modalNonces[modalId] = nonce;
      return;
    }
    this.clearModalNonce(modalId);
  }

  private getModalNonce(modalId: string): string | undefined {
    return this.modalNonces[modalId];
  }

  private clearModalNonce(modalId: string): void {
    delete this.modalNonces[modalId];
  }

  private requireModalNonce(modalId: string): string | undefined {
    const nonce = this.getModalNonce(modalId);
    if (nonce || !ChatWidgetComponent.NONCE_PROTECTED_MODAL_IDS.has(modalId)) {
      this.modalSubmitError.set(null);
      return nonce;
    }

    this.tracking.error('validation');
    this.modalSubmitError.set(this.i18n.t('chat.modalNonceMissing'));
    if (isDevMode()) {
      console.warn('[CHAT-WIDGET] Modal submit blocked because nonce is missing:', modalId);
    }
    return undefined;
  }

  private localizedQueuedAction(message: string): string | undefined {
    const key = ChatWidgetComponent.ACTION_TRANSLATION_KEYS[message.trim()];
    return key ? this.i18n.t(key) : undefined;
  }

  private actionTranslationKey(action: ChatAction): string | undefined {
    if (action.type === 'quick_action' && action.actionType === 'contact') return 'chat.actions.contactForm';
    if (action.modalId) {
      const modalKey = ChatWidgetComponent.ACTION_MODAL_KEYS[action.modalId];
      if (modalKey) {
        return modalKey;
      }
    }

    return ChatWidgetComponent.ACTION_TRANSLATION_KEYS[action.payload?.trim()]
      ?? ChatWidgetComponent.ACTION_TRANSLATION_KEYS[action.label?.trim()];
  }
}
